import {
  Box,
  useTheme,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import { useStateContext } from "contexts/ContextProvider";
import React, { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid2";

import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { OVERLAY_NAMES } from "constants/Overlays";
import api from "services/api";
import { useEditLock } from "hooks/useEditLock";
import EditLockDialog from "components/EditLockDialog";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";

const RequestorNewEdit = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");

  const { overlayData, setOverlayName, triggerPageRefresh } = useStateContext();
  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [isEditable, setIsEditable] = useState(true);

  const { currentUser } = useAuth();
  const { socket } = useWebSocket();

  const isNewRequestor =
    overlayData.Action === OVERLAY_NAMES.REQUESTOR_ACTION_NEW;

  // Edit lock for existing requestors only
  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "requestor",
    isNewRequestor ? null : overlayData.Requestor?.id,
  );

  const [name, setName] = useState(
    isNewRequestor ? "" : overlayData.Requestor.Name || "",
  );
  const [phoneNumber, setPhoneNumber] = useState(
    isNewRequestor ? "" : overlayData.Requestor.PhoneNumber || "",
  );
  const [ext, setExt] = useState(
    isNewRequestor ? "" : overlayData.Requestor.Ext || "",
  );
  const [email, setEmail] = useState(
    isNewRequestor ? "" : overlayData.Requestor.Email || "",
  );

  const [isInvoicer, setIsInvoicer] = useState(
    isNewRequestor ? false : overlayData.Requestor.IsInvoicer,
  );
  const [isMain, setIsMain] = useState(
    overlayData.Action === OVERLAY_NAMES.REQUESTOR_ACTION_NEW
      ? false
      : overlayData.Requestor.IsMain,
  );

  const [nameError, setNameError] = useState(false);
  const [phoneNumberError, setPhoneNumberError] = useState(false);
  const [emailError, setEmailError] = useState(false);

  const [shouldValidate, setShouldValidate] = useState(false);

  const validateFields = useCallback(() => {
    setNameError(!name || name.trim() === "");
    setPhoneNumberError(false);
    setEmailError(false);
  }, [name]);

  const addNewRequestor = useCallback(() => {
    setIsSavingChanges(true);
    setIsEditable(false);

    api
      .post("/clients/addNewRequestor", {
        data: {
          Name: name.trim(),
          PhoneNumber: phoneNumber ? phoneNumber.trim() : "",
          Ext: ext ? ext.trim() : "",
          Email: email ? email.trim() : "",
          IsMain: isMain,
          IsInvoicer: isInvoicer,
          ClientID: overlayData.ClientID,
        },
      })
      .then((response) => {
        setIsSavingChanges(false);

        const clientId = overlayData?.ClientID;
        const changeAction = "Created";
        if (socket && clientId) {
          socket.emit("entity:saved", {
            modelType: "Client",
            recordId: clientId,
            changeType: "Requestor",
            changeAction,
            user: {
              id: currentUser?.id,
              name: currentUser?.Name || "User",
              imageThumbnail: currentUser?.ImageThumbnail,
            },
          });
        }

        triggerPageRefresh("New Requestor Added Successfully!", theme);

        setOverlayName("");
      })
      .catch((err) => console.log(err));
  }, [
    currentUser?.ImageThumbnail,
    currentUser?.Name,
    currentUser?.id,
    ext,
    email,
    isInvoicer,
    isMain,
    name,
    overlayData?.ClientID,
    setOverlayName,
    phoneNumber,
    socket,
    theme,
    triggerPageRefresh,
  ]);

  const updateRequestor = useCallback(() => {
    setIsSavingChanges(true);
    setIsEditable(false);

    api
      .post("/clients/updateRequestor", {
        data: {
          RequestorID: overlayData.Requestor.id,
          Name: name.trim(),
          PhoneNumber: phoneNumber ? phoneNumber.trim() : "",
          Ext: ext ? ext.trim() : "",
          Email: email ? email.trim() : "",
          IsMain: isMain,
          IsInvoicer: isInvoicer,
          ClientID: overlayData.ClientID,
        },
      })
      .then((response) => {
        setIsSavingChanges(false);

        const clientId = overlayData?.ClientID;
        const changeAction = "Updated";
        if (socket && clientId) {
          socket.emit("entity:saved", {
            modelType: "Client",
            recordId: clientId,
            changeType: "Requestor",
            changeAction,
            user: {
              id: currentUser?.id,
              name: currentUser?.Name || "User",
              imageThumbnail: currentUser?.ImageThumbnail,
            },
          });
        }

        triggerPageRefresh("Requestor Updated Sucessfully", theme);

        setOverlayName("");
      })
      .catch((err) => console.log(err));
  }, [
    currentUser?.ImageThumbnail,
    currentUser?.Name,
    currentUser?.id,
    ext,
    email,
    isInvoicer,
    isMain,
    name,
    overlayData?.ClientID,
    overlayData?.Requestor?.id,
    setOverlayName,
    phoneNumber,
    socket,
    theme,
    triggerPageRefresh,
  ]);

  useEffect(() => {
    if (shouldValidate) {
      if (!nameError && !phoneNumberError && !emailError) {
        overlayData.Action === OVERLAY_NAMES.REQUESTOR_ACTION_NEW
          ? addNewRequestor()
          : updateRequestor();
      }
      setShouldValidate(false);
    }
  }, [
    addNewRequestor,
    emailError,
    nameError,
    overlayData.Action,
    phoneNumberError,
    shouldValidate,
    updateRequestor,
  ]);

  const saveRequestor = useCallback(() => {
    validateFields();
    setShouldValidate(true);
  }, [validateFields]);

  const attemptSave = useCallback(() => {
    if (isSavingChanges || isLocked) {
      return;
    }

    saveRequestor();
  }, [isLocked, isSavingChanges, saveRequestor]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const target = e.target;
      const tagName = target?.tagName;
      const isTextArea = tagName === "TEXTAREA";
      const isContentEditable = !!target?.isContentEditable;
      const isInsideDialog =
        target instanceof Element && !!target.closest('[role="dialog"]');

      if (
        e.key === "Enter" &&
        !e.shiftKey &&
        !isTextArea &&
        !isContentEditable &&
        !isInsideDialog
      ) {
        e.preventDefault();
        attemptSave();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [attemptSave]);

  return (
    <Box
      bgcolor="#fff"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.default,
          width: { xs: "100%", sm: "90%", md: "75%", lg: "60%" },
          minWidth: { xs: "100%", sm: "600px" },
          maxWidth: "1200px",
          display: "flex",
          borderRadius: "1.5rem",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
          p: "0rem .2rem 1.5rem 0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            p: "0.5rem 2rem",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              width: "60%",
              flexDirection: "row",
              gap: 1,
            }}
          >
            <IconButton
              color="primary"
              size="large"
              onClick={() => setOverlayName("")}
            >
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>

            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
              {overlayData.Action === OVERLAY_NAMES.REQUESTOR_ACTION_NEW
                ? "New Requestor"
                : "Edit Requestor"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
              width: "40%",
              flexDirection: "row",
              gap: 1,
            }}
          >
            {isMobile ? (
              <Tooltip title="Discard" placement="top">
                <IconButton
                  color="primary"
                  onClick={() => setOverlayName("")}
                  sx={{
                    width: "40px",
                    height: "40px",
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: "12px",
                  }}
                >
                  <DisabledByDefaultRoundedIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                color="primary"
                variant="outlined"
                size="large"
                endIcon={<DisabledByDefaultRoundedIcon />}
                onClick={() => setOverlayName("")}
                sx={{
                  borderRadius: "12px",
                }}
              >
                Discard
              </Button>
            )}
            {isMobile ? (
              <Tooltip title="Save" placement="top">
                <IconButton
                  color="primary"
                  onClick={attemptSave}
                  disabled={isSavingChanges || isLocked}
                  sx={{
                    width: "40px",
                    height: "40px",
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.background.default,
                    borderRadius: "12px",
                    "&:hover": {
                      backgroundColor: theme.palette.primary.dark,
                    },
                  }}
                >
                  <SaveRoundedIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                loading={isSavingChanges}
                disabled={isSavingChanges || isLocked}
                color="primary"
                variant="contained"
                size="large"
                endIcon={<SaveRoundedIcon />}
                type="submit"
                onClick={attemptSave}
                sx={{
                  borderRadius: "12px",
                }}
              >
                Save
              </Button>
            )}
          </Box>
        </Box>

        <Box
          component="form"
          sx={{
            bgcolor: theme.palette.background.alt,
            border: `1px solid ${theme.palette.primary.border2}`,
            borderRadius: "1.5rem",
            display: "flex",
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            overflowY: "auto",
            width: "100%",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
              height: "90%",
            },
          }}
        >
          <Grid container spacing={2} width="100%">
            <Grid size={{ xs: 12 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id={"Requestor_Name"}
                label="Name"
                error={nameError}
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  setNameError(
                    !event.target.value || event.target.value.trim() === "",
                  );
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id={"Requestor_PhoneNumber"}
                label="Phone Number"
                error={phoneNumberError}
                value={phoneNumber}
                onChange={(event) => {
                  setPhoneNumber(event.target.value);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id={"Requestor_Ext"}
                label="Ext"
                value={ext}
                onChange={(event) => {
                  setExt(event.target.value);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id={"Requestor_Email"}
                label="Email"
                error={emailError}
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                value="Main Requestor"
                control={
                  <Checkbox
                    disabled={!isEditable}
                    label="Main Requestor"
                    checked={isMain}
                    onChange={(change) => setIsMain(!isMain)}
                  />
                }
                label="Main Requestor"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                value="Invoice Requestor"
                control={
                  <Checkbox
                    disabled={!isEditable}
                    label="Invoice Requestor"
                    checked={isInvoicer}
                    onChange={(change) => setIsInvoicer(!isInvoicer)}
                  />
                }
                label="Invoice Requestor"
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Edit Lock Dialog */}
      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </Box>
  );
};

export default RequestorNewEdit;
